import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";

// Soft UI Dashboard React components
import SoftBox from "./SoftBox";
import SoftTypography from "./SoftTypography";
import SoftInput from "./SoftInput";
import SoftButton from "./SoftButton";
import CoverLayout from "./CoverLayout";

import { auth } from "../firebase";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import curved9 from "../../public/curved-images/curved-6.jpg";

// Our own page for choosing a new password.
//
// Firebase hosts an equivalent page on <project>.firebaseapp.com, which
// corporate mail and network filters tend to block. The reset email points
// here instead and carries only the one-time oobCode, so nothing the
// recruiter clicks leaves our domain.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = params.get("oobCode");

  const [checking, setChecking] = useState(true);
  const [accountEmail, setAccountEmail] = useState(null);
  const [formData, setFormData] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Check the code before showing the form, so an expired or already-used
  // link says so straight away rather than after they have typed a password.
  useEffect(() => {
    if (!oobCode) {
      setError("This link is incomplete. Request a new one from the sign-in page.");
      setChecking(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setAccountEmail(email);
        setChecking(false);
      })
      .catch(() => {
        setError(
          "This link has expired or has already been used. Request a new one from the sign-in page."
        );
        setChecking(false);
      });
  }, [oobCode]);

  const handleFormData = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const submitFormData = async (e) => {
    e.preventDefault();
    setError("");

    if ((formData.password || "").length < 6) {
      setError("Use at least 6 characters.");
      return;
    }
    if (formData.password !== formData.confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode, formData.password);
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      console.error("confirmPasswordReset failed", err);
      setError(
        "That did not work. The link may have expired — request a new one from the sign-in page."
      );
    } finally {
      setSaving(false);
    }
  };

  const label = (text) => (
    <SoftBox mb={1} ml={0.5}>
      <SoftTypography component="label" variant="caption" fontWeight="bold" color="gt_primary">
        {text}
      </SoftTypography>
    </SoftBox>
  );

  const message = (text, colour) => (
    <SoftBox mt={2} mb={2} textAlign="center">
      <h6
        style={{
          fontSize: ".8em",
          color: colour,
          textAlign: "center",
          fontWeight: 400,
          transition: ".2s all",
        }}
      >
        {text}
      </h6>
    </SoftBox>
  );

  const body = () => {
    if (checking) {
      return (
        <SoftBox display="flex" justifyContent="center">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </SoftBox>
      );
    }

    if (done) {
      return (
        <SoftBox textAlign="center">
          {message("Your password has been updated. Taking you to sign in…", "#1d9e75")}
          <SoftBox mt={2} mb={1}>
            <SoftButton
              variant="contained"
              color="gt_primary"
              fullWidth
              onClick={() => navigate("/login")}
            >
              sign in
            </SoftButton>
          </SoftBox>
        </SoftBox>
      );
    }

    // Code never verified, so there is no form to show.
    if (!accountEmail) {
      return (
        <SoftBox textAlign="center">
          {message(error, "red")}
          <SoftBox mt={2} mb={1}>
            <SoftButton
              variant="contained"
              color="gt_primary"
              fullWidth
              onClick={() => navigate("/login")}
            >
              back to sign in
            </SoftButton>
          </SoftBox>
        </SoftBox>
      );
    }

    return (
      <SoftBox component="form" role="form" onSubmit={submitFormData}>
        <SoftBox mb={2} textAlign="center">
          <SoftTypography variant="button" fontWeight="regular" color="text">
            {accountEmail}
          </SoftTypography>
        </SoftBox>

        <SoftBox mb={2}>
          {label("New password")}
          <SoftInput
            type="password"
            name="password"
            value={formData.password}
            onChange={handleFormData}
            placeholder="New password"
          />
        </SoftBox>

        <SoftBox mb={2}>
          {label("Confirm new password")}
          <SoftInput
            type="password"
            name="confirm"
            value={formData.confirm}
            onChange={handleFormData}
            placeholder="Confirm new password"
          />
        </SoftBox>

        {error && message(error, "red")}

        <SoftBox mt={4} mb={1}>
          <SoftButton
            variant="contained"
            color="gt_primary"
            onClick={submitFormData}
            fullWidth
            disabled={saving}
          >
            {saving ? "saving…" : "set password"}
          </SoftButton>
        </SoftBox>

        <SoftBox mt={1} mb={1} textAlign="center">
          <SoftTypography
            variant="button"
            color="gt_primary"
            fontWeight="medium"
            onClick={() => navigate("/login")}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            Back to sign in
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    );
  };

  return (
    <CoverLayout
      color="gt_primary"
      title="Choose a new password"
      description={accountEmail ? "" : " "}
      image={curved9}
      top="30px"
      style={{ textAlign: "center", paddingLeft: "1rem" }}
    >
      {body()}
    </CoverLayout>
  );
}
